'use client'

import classnames from 'classnames'
import styles from './NavigationItems.module.scss'
import { useContext, useState } from 'react'
import { GlobalSettingsContext } from '@/context/GlobalSettings'
import classNames from 'classnames'
import Clock from '@/components/Clock/Clock'
import Link from '@/components/Link/Link'
import DownArrow from '@/components/_svgs/DownArrow'
import useStore from '@/store'

type NavigationItemsProps = {
  className?: string
  isInNavigation?: boolean
}

export const ROMAN_NUMERALS = {
  0: 'i',
  1: 'ii',
  2: 'iii',
  3: 'iv',
  4: 'v',
  5: 'vi',
  6: 'vii',
  7: 'viii',
  8: 'ix',
  9: 'x',
}

const NavigationItems = ({ className, isInNavigation = true }: NavigationItemsProps) => {
  const { navigationData } = useContext(GlobalSettingsContext)

  if (!navigationData) return null

  return (
    <div className={classnames(styles.NavigationItems, className, { [styles.isInNavigation]: isInNavigation })}>
      <div className={styles.inner}>
        <div className={styles.locationAndAbout}>
          <LocationContent />
          <AboutContent />
        </div>
        <div className={styles.servicesExpertiseContact}>
          <ServicesContent />
          <ExpertiseContent />
          <ContactMeContent />
        </div>
      </div>
    </div>
  )
}

const ServicesContent = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.servicesContent, className)}>
      <TitleWithArrow
        className={styles.servicesContent__title}
        label={navigationData.services.title}
      />
      <ul className={styles.servicesContent__list}>
        {navigationData.services.links.map((link, i) => (
          <li
            key={i}
            className={styles.servicesContent__list__item}
          >
            <Link
              link={link}
              className={styles.servicesContent__list__link}
              onClick={() => {
                setNavIsOpen(false)
              }}
            >
              <span className={styles.servicesContent__list__romanNumber}>
                {ROMAN_NUMERALS[i as keyof typeof ROMAN_NUMERALS]}
              </span>
              <span className={styles.servicesContent__list__linkText}>{link.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

const ExpertiseContent = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.expertiseContent, className)}>
      <TitleWithArrow
        className={styles.expertiseContent__title}
        label={navigationData.expertise.title}
      />
      <ul className={styles.expertiseContent__list}>
        {navigationData.expertise.links.map((link, i) => (
          <li
            key={i}
            className={styles.expertiseContent__list__item}
          >
            <Link
              link={link}
              className={styles.expertiseContent__list__link}
              onClick={() => {
                setNavIsOpen(false)
              }}
            >
              <span className={styles.expertiseContent__list__number}>0{i + 1}</span>
              <span className={styles.expertiseContent__list__linkText}>{link.label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const LocationHeader = ({ className, allowClock }: { className?: string; allowClock: boolean }) => {
  const { navigationData } = useContext(GlobalSettingsContext)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.locationContent__header, className)}>
      <p className={styles.locationTitle}>{navigationData.location.title}</p>
      {allowClock && <Clock />}
    </div>
  )
}

const LocationContent = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.locationContent, className)}>
      <LocationHeader
        className={styles.navColumnHeader}
        allowClock={true}
      />
      <div className={styles.locationContent__address}>
        <p className={classnames(styles.locationContent__address__title, styles.locationAboutFirstContent)}>
          <span className={styles.locationContent__address__title__definition}>a:</span>
          <span className={styles.locationContent__address__title__content}>
            {navigationData.location.address}
            <br />
            <Link
              link={{
                linkType: 'external',
                link: navigationData.location.addressLink,
                label: 'Directions',
              }}
              onClick={() => {
                setNavIsOpen(false)
              }}
              className={styles.locationContent__address__link}
            />
          </span>
        </p>
        <p className={styles.locationContent__phoneNumber__title}>
          <span className={styles.locationContent__phoneNumber__title__definition}>p:</span>
          <span className={styles.locationContent__phoneNumber__title__content}>
            {navigationData.about.phoneNumber}
          </span>
        </p>
        <p className={styles.locationContent__email__title}>
          <span className={styles.locationContent__email__title__definition}>e:</span>
          <span className={styles.locationContent__email__title__content}>
            <Link
              onClick={() => {
                setNavIsOpen(false)
              }}
              link={{
                linkType: 'external',
                link: `mailto:${navigationData.about.email}`,
                label: navigationData.about.email,
              }}
            />
          </span>
        </p>
      </div>
    </div>
  )
}

export const ContactMeLink = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)
  const [isSamePage, setIsSamePage] = useState(false)

  if (!navigationData) return null

  return (
    <Link
      onClick={() => {
        setNavIsOpen(false)
      }}
      getIsSamePage={isSame => {
        setIsSamePage(isSame)
      }}
      link={navigationData.contactLink}
      className={classNames(styles.contactMeContent__link, className, { [styles.inactive]: isSamePage })}
    >
      <span className={styles.contactMeContent__link__text}>{navigationData.contactLink.label}</span>
      <svg
        viewBox="0 0 92 5"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.contactMeContent__link__svg}
      >
        <path
          d="M91.5321 0.568847L0.0380859 0.568848L0.038086 1.84685L88.2741 1.84685C87.7161 2.47685 87.1221 3.44885 86.5461 4.72685L87.5721 4.72685C88.8321 3.25085 90.1641 2.17085 91.5321 1.46885L91.5321 0.568847Z"
          fill="currentColor"
        />
      </svg>
    </Link>
  )
}

const ContactMeContent = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.contactMeContent, className)}>
      <ContactMeLink />
    </div>
  )
}

const AboutContent = ({ className }: { className?: string }) => {
  const { navigationData } = useContext(GlobalSettingsContext)
  const setNavIsOpen = useStore(state => state.setNavIsOpen)

  if (!navigationData) return null

  return (
    <div className={classNames(styles.aboutContent, className)}>
      <TitleWithArrow
        label={navigationData.about.title}
        className={styles.aboutContent__title}
      />
      <ul className={classnames(styles.aboutContent__list, styles.locationAboutFirstContent)}>
        {navigationData.about.links.map((item, i) => (
          <li
            key={i}
            className={styles.aboutContent__list__item}
          >
            <Link
              link={item}
              className={styles.aboutContent__list__link}
              onClick={() => {
                setNavIsOpen(false)
              }}
            />
          </li>
        ))}
      </ul>
      <p className={styles.aboutContent__clientLogin}>
        <Link
          link={navigationData.clientLoginLink}
          className={styles.aboutContent__clientLogin__link}
          onClick={() => {
            setNavIsOpen(false)
          }}
        />
      </p>
    </div>
  )
}

const TitleWithArrow = ({ className, label }: { className?: string; label: string }) => (
  <p className={classnames(className, styles.titleWithArrow, styles.navColumnHeader)}>
    {label}{' '}
    <span className={styles.titleWithArrow__downArrow}>
      <DownArrow />
    </span>
  </p>
)

NavigationItems.displayName = 'NavigationItems'

export default NavigationItems
